import { t } from "app/utils";

import answerSurvey from "images/badges/answer-survey.png";
import admin from "images/badges/admin.png";
import attendAnyEvent from "images/badges/attend-any-event.png";
import book from "images/badges/book.png";
import charity from "images/badges/charity.png";
import coffee from "images/badges/coffee.png";
import createStory from "images/badges/create-story.png";
import createSurvey from "images/badges/create-survey.png";
import culturehqBot from "images/badges/culturehqbot.png";
import department from "images/badges/department.png";
import eventWith2NewAttendees from "images/badges/event-with-2-new-attendees.png";
import fillOutProfile from "images/badges/fill-out-profile.png";
import giveRecognition from "images/badges/give-recognition.png";
import hobbies from "images/badges/hobbies.png";
import inviteColleagues from "images/badges/invite-colleagues-to-join-chq.png";
import joiningCHQ from "images/badges/joining-chq.png";
import learningAndDevelopment from "images/badges/learning-and-development.png";
import lunch from "images/badges/lunch.png";
import receiveRecognition from "images/badges/receive-recognition.png";
import social from "images/badges/social.png";
import shareSocialBadge from "images/badges/share-social-badge.svg";
import reshareSocialBadge from "images/badges/reshare-social-badge.svg";
import reactionSocialBadge from "images/badges/reaction-social-badge.svg";
import commentSocialBadge from "images/badges/comment-social-badge.svg";
import storyComment from "images/badges/story-comment.png";
import uploadPhoto from "images/badges/upload-photo.png";
import wellness from "images/badges/wellness.png";

import * as CHQ from "chq";

type BadgeType = {
  key: string;
  camel: string;
  description: string;
  past: string;
  category?: string;
  image: string;
  points: number | null;
};

function isBadgeType<T extends BadgeType | false>(value: T): value is Exclude<T, false> {
  return Boolean(value);
}

export const eventSurveyBadge: BadgeType = {
  key: "event_survey",
  camel: "eventSurvey",
  description: t("Attend event and complete post-event survey."),
  past: t("Attended event and completed post-event survey."),
  category: "events",
  image: attendAnyEvent,
  points: 25
};

export const widgetSurveyBadge: BadgeType = {
  key: "widget_survey",
  camel: "widgetSurvey",
  description: t("Complete a survey."),
  past: t("Completed a survey."),
  category: "general",
  image: answerSurvey,
  points: 50
};

const getBadgeTypes = (profile: CHQ.Profile): BadgeType[] => {
  const {
    name = "your organization",
    eventsEnabled,
    recognitionsEnabled,
    surveysEnabled,
    userCreatedInvitesEnabled,
    userCreatedSurveysEnabled,
    userCreatedEventsEnabled
  } = profile.organization;

  return [
    {
      key: "complete_profile",
      camel: "completeProfile",
      description: t("Complete your full profile."),
      past: t("Completed your full profile."),
      category: "general",
      image: fillOutProfile,
      points: 100
    },
    userCreatedInvitesEnabled && {
      key: "create_invite",
      camel: "createInvite",
      description: t(`Invite a new user to join your ${name} CultureHQ community.`),
      past: t(`Invited a new user to join your ${name} CultureHQ community.`),
      category: "invite",
      image: inviteColleagues,
      points: 100
    },
    {
      key: "joining",
      camel: "joining",
      description: t(`Join the ${name} CultureHQ community.`),
      past: t(`Joined the ${name} CultureHQ community.`),
      category: "general",
      image: joiningCHQ,
      points: 100
    },
    recognitionsEnabled && {
      key: "auto_recognition",
      camel: "autoRecognition",
      description: t("Receive recognition from CultureBot for completing a specific action multiple times."),
      past: t("Received recognition from CultureBot for completing a specific action multiple times."),
      category: "recognition",
      image: culturehqBot,
      points: 100
    },
    eventsEnabled && userCreatedEventsEnabled && {
      key: "host_l_and_d",
      camel: "hostLAndD",
      description: t("Create and host a learning and development event."),
      past: t("Created and hosted a learning and development."),
      category: "events",
      image: learningAndDevelopment,
      points: 75
    },
    eventsEnabled && userCreatedEventsEnabled && {
      key: "event_with_two_new",
      camel: "eventWithTwoNew",
      description: t("Host an event with 2+ users who did not attend an event you previously hosted."),
      past: t("Created an event with 2+ new attendees."),
      category: "invite",
      image: eventWith2NewAttendees,
      points: 75
    },
    {
      key: "interests",
      camel: "interests",
      description: t("Add 3+ hobbies and interests."),
      past: t("Added 3+ hobbies and interests."),
      category: "general",
      image: hobbies,
      points: 50
    },
    surveysEnabled && widgetSurveyBadge,
    surveysEnabled && userCreatedSurveysEnabled && {
      key: "widget_survey_activated",
      camel: "widgetSurveyActivated",
      description: t("Create and activate a survey."),
      past: t("Created and activated a survey."),
      category: "general",
      image: createSurvey,
      points: 50
    },
    eventsEnabled && userCreatedEventsEnabled && {
      key: "host_charity",
      camel: "hostCharity",
      description: t("Create and host a charity event."),
      past: t("Created and hosted a charity event."),
      category: "events",
      image: charity,
      points: 50
    },
    eventsEnabled && userCreatedEventsEnabled && {
      key: "host_social",
      camel: "hostSocial",
      description: t("Create and host a social event."),
      past: t("Created and hosted a social event."),
      category: "events",
      image: social,
      points: 50
    },
    eventsEnabled && userCreatedEventsEnabled && {
      key: "host_wellness",
      camel: "hostWellness",
      description: t("Create and host a wellness event."),
      past: t("Created and hosted a wellness event."),
      category: "events",
      image: wellness,
      points: 50
    },
    recognitionsEnabled && {
      key: "give_recognition",
      camel: "giveRecognition",
      description: t("Give recognition to a user."),
      past: t("Gave recognition to a user."),
      category: "recognition",
      image: giveRecognition,
      points: 50
    },
    eventsEnabled && {
      key: "meet_other_department",
      camel: "meetOtherDepartment",
      description: t("Host or attend a 1-on-1 event with a user from another department."),
      past: t("Hosted or attended a 1-on-1 event with a user from another department."),
      category: "invite",
      image: department,
      points: 50
    },
    recognitionsEnabled && {
      key: "recognition",
      camel: "recognition",
      description: t("Receive recognition from a user."),
      past: t("Recognized by a user."),
      category: "recognition",
      image: receiveRecognition,
      points: 50
    },
    eventsEnabled && eventSurveyBadge,
    eventsEnabled && userCreatedEventsEnabled && {
      key: "host_coffee",
      camel: "hostCoffee",
      description: t("Create and host a coffee event."),
      past: t("Created and hosted a coffee event."),
      category: "events",
      image: coffee,
      points: 25
    },
    eventsEnabled && userCreatedEventsEnabled && {
      key: "host_lunch",
      camel: "hostLunch",
      description: t("Create and host a lunch event."),
      past: t("Created and hosted a lunch event."),
      category: "events",
      image: lunch,
      points: 25
    },
    eventsEnabled && {
      key: "upload_photo",
      camel: "uploadPhoto",
      description: t("Upload a photo to an event."),
      past: t("Uploaded a photo to an event."),
      category: "events",
      image: uploadPhoto,
      points: 25
    },
    {
      key: "answer_story_question",
      camel: "answerStoryQuestion",
      description: t("Answer a story."),
      past: t("Answered a story."),
      category: "stories",
      image: book,
      points: 50
    },
    {
      key: "comment_story",
      camel: "commentStory",
      description: t("Comment on a user's story."),
      past: t("Commented on a user's story."),
      category: "stories",
      image: storyComment,
      points: 5
    },
    {
      key: "create_story_question",
      camel: "createStoryQuestion",
      description: t("Started a new story trend."),
      past: t("Started a new story trend."),
      category: "stories",
      image: createStory,
      points: 50
    },
    {
      key: "create_linkedin_share",
      camel: "createLinkedinShare",
      description: t("Share a post on LinkedIn."),
      past: t("Shared a post on LinkedIn."),
      category: "social",
      image: shareSocialBadge,
      points: 100
    },
    {
      key: "comment_linkedin_share",
      camel: "commentLinkedinShare",
      description: t("Comment on a LinkedIn post."),
      past: t("Commented a LinkedIn post."),
      category: "social",
      image: commentSocialBadge,
      points: 25
    },
    {
      key: "react_linkedin_share",
      camel: "reactLinkedinShare",
      description: t("React to a LinkedIn post."),
      past: t("Reacted to a LinkedIn post."),
      category: "social",
      image: reactionSocialBadge,
      points: 10
    },
    {
      key: "reshare_linkedin_share",
      camel: "reshareLinkedinShare",
      description: t("Share a LinkedIn post."),
      past: t("Shared a LinkedIn post."),
      category: "social",
      image: reshareSocialBadge,
      points: 50
    },
    {
      key: "create_tiktok_share",
      camel: "createTiktokShare",
      description: t("Share a post on TikTok."),
      past: t("Shared a post on TikTok."),
      category: "social",
      image: shareSocialBadge,
      points: 100
    },
    {
      key: "create_facebook_share",
      camel: "createFacebookShare",
      description: t("Share a post on Facebook."),
      past: t("Shared a post on Facebook."),
      category: "social",
      image: shareSocialBadge,
      points: 100
    },
    {
      key: "create_instagram_share",
      camel: "createInstagramShare",
      description: t("Share a post on Instagram."),
      past: t("Shared a post on Instagram."),
      category: "social",
      image: shareSocialBadge,
      points: 100
    },
    {
      key: "arbitrary",
      camel: "arbitrary",
      description: t("Receive a community score bonus by an admin."),
      past: t("Awarded a community score bonus by an admin."),
      detail: t("Awarded a community score bonus for:"),
      image: admin,
      points: null
    }
  ].filter(isBadgeType);
};

export default getBadgeTypes;
